import { useEffect, useRef } from "react"
import { useBlocker } from "react-router"
import { DestructiveConfirmation } from "./destructive-confirmation.tsx"
import { hasUnsavedWork, leavesPage, type At } from "./unsaved-work.ts"

/*
 * What keeps a page's unsaved work (`hasUnsavedWork()`) from being thrown away by a move away from
 * it: a link followed in place, Back, a crumb or a tab asks "Discard changes?" first, and stays
 * where it is unless the Member discards. `leaves` says which moves leave what holds the work: by
 * default a move to another page (`leavesPage`), so a change to the page's own address (a comment's
 * anchor) asks nothing. Leaving the web app altogether (a reload, closing the tab, typing another
 * address) is the browser's to ask about, through `beforeunload`, in its own words, unless
 * `unload` is false, as for the Ticket peek, which leaves that to go without asking.
 *
 * `what` names the work in the question, a ticket's description or comment unless the page says.
 *
 * A router allows one such guard at a time, so a page renders one: the ticket's page, or the
 * Ticket peek over a view.
 */
export function UnsavedWorkGuard({
  leaves = leavesPage,
  unload = true,
  what,
}: {
  leaves?: (from: At, to: At) => boolean
  unload?: boolean
  what?: string
}) {
  const discarding = useRef(false)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedWork() && leaves(currentLocation, nextLocation),
  )
  if (blocker.state === "unblocked") discarding.current = false

  useEffect(() => {
    if (!unload) return
    const asking = (event: BeforeUnloadEvent) => {
      if (hasUnsavedWork()) event.preventDefault()
    }
    window.addEventListener("beforeunload", asking)
    return () => window.removeEventListener("beforeunload", asking)
  }, [unload])

  return (
    <DestructiveConfirmation
      removes="unsaved-text"
      what={what}
      trigger="none"
      navigationBlocker
      open={blocker.state === "blocked"}
      // The confirmation closes itself once Discard has gone ahead, which mustn't take it back.
      onOpenChange={(opened) => !opened && !discarding.current && blocker.reset?.()}
      onConfirm={async () => {
        discarding.current = true
        blocker.proceed?.()
      }}
    />
  )
}
